import React, { useEffect, useMemo, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  ArrowRight,
  Book,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  Database,
  FileText,
  Globe,
  Info,
  Key,
  Search,
  Shield,
  Terminal,
  Zap,
} from "lucide-react";
import MortgageChainFooter from "../../components/MortgageChainFooter/MortgageChainFooter";

type CodeLanguage = "curl" | "javascript" | "python" | "ruby";
type EndpointCategory = "Loans" | "Analytics" | "Blockchain";

interface EndpointParameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
  default?: string;
}

interface EndpointDefinition {
  id: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  title: string;
  description: string;
  category: EndpointCategory;
  parameters?: EndpointParameter[];
  response: Record<string, unknown>;
}

interface FeatureCard {
  icon: LucideIcon;
  title: string;
  description: string;
  color: "cyan" | "purple" | "green" | "amber";
}

interface QuickStartStep {
  icon: LucideIcon;
  title: string;
  description: string;
  color: "cyan" | "purple" | "green";
}

const docsNavItems = [
  { id: "docs-overview", label: "Overview", icon: Book },
  { id: "docs-quickstart", label: "Quick Start", icon: Zap },
  { id: "docs-authentication", label: "Authentication", icon: Key },
  { id: "docs-endpoints", label: "Endpoints", icon: Terminal },
  { id: "docs-errors", label: "Errors", icon: AlertCircle },
  { id: "docs-rate-limits", label: "Rate Limits", icon: Clock },
  { id: "docs-webhooks", label: "Webhooks", icon: Globe },
] as const;

const getCodeExample = (
  endpoint: EndpointDefinition,
  lang: CodeLanguage
): string => {
  const baseUrl = "https://api.mortgagechain.ca";
  const fullPath = `${baseUrl}${endpoint.path}`;
  const params =
    endpoint.parameters
      ?.filter((p) => !p.required && p.name !== "loan_id")
      .slice(0, 3) || [];
  const queryString =
    params.length > 0
      ? "?" + params.map((p) => `${p.name}=${p.default || "value"}`).join("&")
      : "";

  switch (lang) {
    case "curl":
      return `curl -X ${endpoint.method} "${fullPath}${queryString}" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`;

    case "javascript":
      return `const response = await fetch('${fullPath}${queryString}', {
  method: '${endpoint.method}',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

const data = await response.json();
console.log(data);`;

    case "python":
      return `import requests

headers = {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
}

response = requests.${endpoint.method.toLowerCase()}(
    '${fullPath}${queryString}',
    headers=headers
)

data = response.json()
print(data)`;

    case "ruby":
      const methodMap: Record<string, string> = {
        GET: "Get",
        POST: "Post",
        PUT: "Put",
        DELETE: "Delete",
      };
      const methodName = methodMap[endpoint.method] || "Get";
      return `require 'net/http'
require 'json'

uri = URI('${fullPath}${queryString}')
request = Net::HTTP::${methodName}.new(uri)
request['Authorization'] = 'Bearer YOUR_API_KEY'
request['Content-Type'] = 'application/json'

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

data = JSON.parse(response.body)
puts data`;
  }
};

const codeExamples: Record<CodeLanguage, string> = {
  curl: `curl -X GET "https://api.mortgagechain.ca/v1/loans?province=ON&limit=100" \\
-H "Authorization: Bearer YOUR_API_KEY" \\
-H "Content-Type: application/json"`,
  javascript: `const response = await fetch('https://api.mortgagechain.ca/v1/loans?province=ON&limit=100', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

const data = await response.json();
console.log(data);`,
  python: `import requests

headers = {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
}

response = requests.get(
    'https://api.mortgagechain.ca/v1/loans',
    headers=headers,
    params={'province': 'ON', 'limit': 100}
)

data = response.json()
print(data)`,
  ruby: `require 'net/http'
require 'json'

uri = URI('https://api.mortgagechain.ca/v1/loans?province=ON&limit=100')
request = Net::HTTP::Get.new(uri)
request['Authorization'] = 'Bearer YOUR_API_KEY'
request['Content-Type'] = 'application/json'

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

data = JSON.parse(response.body)
puts data`,
};

const endpoints: EndpointDefinition[] = [
  {
    id: "get-loans",
    method: "GET",
    path: "/v1/loans",
    title: "List Loans",
    description:
      "Retrieve a paginated list of anonymized mortgage loans with optional filters.",
    category: "Loans",
    parameters: [
      {
        name: "limit",
        type: "integer",
        required: false,
        default: "100",
        description: "Number of results per page (max 1000)",
      },
      {
        name: "offset",
        type: "integer",
        required: false,
        default: "0",
        description: "Pagination offset",
      },
      {
        name: "province",
        type: "string",
        required: false,
        description: "Filter by province (ON, BC, AB, etc.)",
      },
    ],
    response: {
      loans: [
        {
          id: "loan_a1b2c3d4",
          amount: 450000,
          ltv: 0.75,
          rate: 4.5,
          term_months: 60,
          province: "ON",
          property_type: "single_family",
        },
      ],
      pagination: {
        total: 10243,
        limit: 100,
        offset: 0,
        has_more: true,
      },
    },
  },
  {
    id: "get-loan",
    method: "GET",
    path: "/v1/loans/{loan_id}",
    title: "Get Loan Details",
    description: "Retrieve detailed information about a specific loan.",
    category: "Loans",
    parameters: [
      {
        name: "loan_id",
        type: "string",
        required: true,
        description: "The unique loan identifier",
      },
    ],
    response: {
      id: "loan_a1b2c3d4",
      amount: 450000,
      ltv: 0.75,
      rate: 4.5,
      term_months: 60,
      province: "ON",
      property_type: "single_family",
      credit_score_range: "720-750",
      employment_type: "full_time",
    },
  },
  {
    id: "get-analytics",
    method: "GET",
    path: "/v1/analytics/market",
    title: "Market Analytics",
    description: "Get aggregated market analytics and trends.",
    category: "Analytics",
    parameters: [
      {
        name: "province",
        type: "string",
        required: false,
        description: "Filter by province",
      },
      {
        name: "period",
        type: "string",
        required: false,
        default: "30d",
        description: "Time period (7d, 30d, 90d, 1y)",
      },
    ],
    response: {
      period: "30d",
      province: "ON",
      metrics: {
        avg_rate: 4.72,
        avg_ltv: 0.73,
        total_volume: 2500000000,
        loan_count: 5234,
      },
      trends: {
        rate_change: -0.15,
        ltv_change: 0.02,
        volume_change: 0.12,
      },
    },
  },
  {
    id: "get-defaults",
    method: "GET",
    path: "/v1/analytics/defaults",
    title: "Default Rates",
    description: "Get default rate statistics and predictions.",
    category: "Analytics",
    parameters: [
      {
        name: "province",
        type: "string",
        required: false,
        description: "Filter by province",
      },
      {
        name: "ltv_range",
        type: "string",
        required: false,
        description: 'LTV range (e.g., "0.7-0.8")',
      },
    ],
    response: {
      current_default_rate: 0.0142,
      historical_avg: 0.0156,
      prediction_30d: 0.0145,
      confidence: 0.87,
      by_ltv_bucket: {
        "0.6-0.7": 0.0089,
        "0.7-0.8": 0.0132,
        "0.8-0.9": 0.0198,
      },
    },
  },
  {
    id: "get-verification",
    method: "GET",
    path: "/v1/blockchain/verify",
    title: "Verify Data Hash",
    description: "Verify data integrity using blockchain Merkle root.",
    category: "Blockchain",
    parameters: [
      {
        name: "data_hash",
        type: "string",
        required: true,
        description: "SHA-256 hash of data to verify",
      },
      {
        name: "timestamp",
        type: "string",
        required: true,
        description: "ISO 8601 timestamp of data",
      },
    ],
    response: {
      verified: true,
      blockchain: "solana",
      merkle_root:
        "0x7f83b1657ff1fc53b92dc18148a1d65dfc2d4b1fa3d677284addd200126d9069",
      block_height: 234567890,
      transaction_id: "5FHneW7pw8aVLSJqFkNQZGdqQQRJUvMPgFqHvKzHGwQ5",
      published_at: "2024-01-01T00:00:00Z",
    },
  },
];

const quickStartSteps: QuickStartStep[] = [
  {
    icon: Key,
    title: "Get Your API Key",
    description:
      "Sign up for a Crypsey account and generate your API key from the dashboard.",
    color: "cyan",
  },
  {
    icon: Terminal,
    title: "Make Your First Request",
    description:
      "Use your API key to authenticate and fetch your first dataset.",
    color: "purple",
  },
  {
    icon: Zap,
    title: "Integrate & Build",
    description: "Start building mortgage analytics into your applications.",
    color: "green",
  },
];

const features: FeatureCard[] = [
  {
    icon: Zap,
    title: "Low Latency",
    description: "<200ms average response time",
    color: "cyan",
  },
  {
    icon: Shield,
    title: "Secure",
    description: "Enterprise-grade encryption",
    color: "purple",
  },
  {
    icon: Database,
    title: "Real-time",
    description: "Daily data updates",
    color: "green",
  },
  {
    icon: Globe,
    title: "RESTful",
    description: "Standard HTTP methods",
    color: "amber",
  },
];

const categories: EndpointCategory[] = ["Loans", "Analytics", "Blockchain"];

const defaultExpandedSections = categories.reduce<Record<string, boolean>>(
  (acc, category) => {
    acc[category.toLowerCase()] = category === "Loans";
    return acc;
  },
  {}
);

export default function MortgageChainAPIDocs() {
  const [activeLang, setActiveLang] = useState<CodeLanguage>("curl");
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(defaultExpandedSections);
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>(
    endpoints[0]?.id ?? ""
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState<string>("docs-overview");
  const sectionIds = useMemo(
    () => ["docs-hero", ...docsNavItems.map((item) => item.id)],
    []
  );

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) =>
              a.target.getBoundingClientRect().top -
              b.target.getBoundingClientRect().top
          )[0];

        if (visibleEntry?.target?.id) {
          setActiveSection(visibleEntry.target.id);
        }
      },
      { rootMargin: "-50% 0px -50% 0px" }
    );

    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [sectionIds]);

  const normalizedActiveSection =
    activeSection === "docs-hero" ? docsNavItems[0].id : activeSection;

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setActiveSection(id);
  };

  const filteredEndpoints = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return endpoints;
    }
    return endpoints.filter((endpoint) => {
      const haystack = [
        endpoint.title,
        endpoint.description,
        endpoint.path,
        endpoint.category,
        endpoint.parameters?.map((param) => param.name).join(" "),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [searchQuery]);

  const endpointsByCategory = useMemo(() => {
    return categories.reduce<Record<EndpointCategory, EndpointDefinition[]>>(
      (acc, category) => {
        acc[category] = filteredEndpoints.filter(
          (endpoint) => endpoint.category === category
        );
        return acc;
      },
      {
        Loans: [],
        Analytics: [],
        Blockchain: [],
      }
    );
  }, [filteredEndpoints]);

  const handleCopyToClipboard = (code: string, id: string) => {
    if (!navigator?.clipboard) return;
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopiedCode(id);
        setTimeout(() => setCopiedCode(null), 2000);
      })
      .catch(() => setCopiedCode(null));
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const emitFormEvent = (type: "call" | "deck") => {
    window.dispatchEvent(
      new CustomEvent("mortgagechain:openForm", { detail: { type } })
    );
  };

  return (
    <div className="bg-[#0A0E27] text-white min-h-screen">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;600&family=Poppins:wght@500;600;700&display=swap');
        * { font-family: 'Inter', sans-serif; }
        h1, h2, h3 { font-family: 'Space Grotesk', sans-serif; }
        .btn-font { font-family: 'Poppins', sans-serif; }
        .font-mono { font-family: 'JetBrains Mono', monospace; }
        .glassmorphic { background: rgba(26,31,58,0.8); backdrop-filter: blur(20px); border: 1px solid rgba(0,212,255,0.2); }
        .glassmorphic-strong { background: rgba(26,31,58,0.95); backdrop-filter: blur(30px); border: 1px solid rgba(0,212,255,0.3); }
        .gradient-text { background: linear-gradient(90deg,#00D4FF 0%,#8B5CF6 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .code-block { background: #0D1117; border: 1px solid #30363D; border-radius: 12px; overflow: hidden; }
        pre { margin: 0; padding: 1rem; overflow-x: auto; }
        code { font-family: 'JetBrains Mono', monospace; font-size: 0.875rem; line-height: 1.5; }
        .tab-active { background: rgba(0,212,255,0.1); border-color: #00D4FF; color: #00D4FF; }
        .method-badge { padding: 0.25rem 0.75rem; border-radius: 0.5rem; font-weight: 600; font-size: 0.75rem; font-family: 'JetBrains Mono', monospace; }
        .method-get { background: rgba(0,255,136,0.1); color: #00FF88; border: 1px solid rgba(0,255,136,0.3); }
        .method-post { background: rgba(0,212,255,0.1); color: #00D4FF; border: 1px solid rgba(0,212,255,0.3); }
        .method-put { background: rgba(255,184,0,0.1); color: #FFB800; border: 1px solid rgba(255,184,0,0.3); }
        .method-delete { background: rgba(255,51,102,0.1); color: #FF3366; border: 1px solid rgba(255,51,102,0.3); }
        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track { background: #0A0E27; }
        ::-webkit-scrollbar-thumb { background: linear-gradient(180deg,#00D4FF 0%,#8B5CF6 100%); border-radius: 10px; }
        .hover-lift { transition: all 0.3s ease; }
        .hover-lift:hover { transform: translateY(-4px); }
      `}</style>

      <section
        id="docs-hero"
        className="relative pt-28 pb-20 px-6 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-purple-500/10" />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 backdrop-blur-sm mb-6">
              <Book className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-semibold text-cyan-400">
                Version 1.0 - RESTful API
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Developer-First <span className="gradient-text">API</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Integrate mortgage data into your platform in minutes. RESTful API
              with comprehensive documentation, Python SDK, and GraphQL support.
              Built for developers who value speed, reliability, and clear
              documentation.
            </p>
            <div className="max-w-2xl mx-auto mb-12">
              <div className="glassmorphic rounded-2xl p-2 flex items-center gap-3">
                <Search className="w-5 h-5 text-gray-400 ml-3" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search endpoints, parameters, or examples..."
                  className="flex-1 bg-transparent border-none outline-none text-white placeholder-gray-500"
                />
                <button className="bg-gradient-to-r from-cyan-500 to-purple-500 px-6 py-2 rounded-xl font-semibold">
                  Search
                </button>
              </div>
            </div>
          </div>

          {/* Developer Features */}
          <div className="glassmorphic rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold mb-6">Built for Developers</h2>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {[
                "Sub-200ms response times with caching",
                "99.9% uptime SLA with monitoring",
                "Comprehensive API documentation with examples",
                "Webhook support for real-time updates",
                "Rate limiting & authentication included",
                "GraphQL for flexible queries",
                "Python & JavaScript SDKs available",
                "Postman collection & code samples",
              ].map((feature, index) => (
                <div key={index} className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3 text-[#00FF88] flex-shrink-0" />
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* API Request Example */}
          <div className="glassmorphic rounded-2xl p-6 mb-8 border border-[#00D4FF]/30">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-mono text-[#00D4FF]">
                API Request Example
              </span>
              <div className="flex space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-[#FFB800]" />
                <div className="w-3 h-3 rounded-full bg-[#00FF88]" />
              </div>
            </div>
            <pre className="font-mono text-xs overflow-x-auto">
              <code className="text-gray-300">
                {`GET /api/v1/loans?
  location=toronto&
  ltv_min=0.7&
  ltv_max=0.8&
  credit_score_min=680

{
  "data": [
    {
      "loan_id": "a9f2c...",
      "amount": 450000,
      "interest_rate": 7.85,
      "ltv": 0.75,
      "location": "M5V",
      "credit_score_range": "680-720",
      "status": "current",
      "blockchain_verified": true,
      "merkle_proof": "0x7f3a..."
    }
  ],
  "meta": {
    "count": 127,
    "verified_on_chain": true
  }
}`}
              </code>
            </pre>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {features.map((feature) => {
              const IconComponent = feature.icon;
              const bgColor =
                feature.color === "cyan"
                  ? "rgba(0, 212, 255, 0.1)"
                  : feature.color === "purple"
                  ? "rgba(139, 92, 246, 0.1)"
                  : feature.color === "green"
                  ? "rgba(0, 255, 136, 0.1)"
                  : "rgba(255, 184, 0, 0.1)";
              const borderColor =
                feature.color === "cyan"
                  ? "rgba(0, 212, 255, 0.3)"
                  : feature.color === "purple"
                  ? "rgba(139, 92, 246, 0.3)"
                  : feature.color === "green"
                  ? "rgba(0, 255, 136, 0.3)"
                  : "rgba(255, 184, 0, 0.3)";
              const iconColor =
                feature.color === "cyan"
                  ? "#00D4FF"
                  : feature.color === "purple"
                  ? "#8B5CF6"
                  : feature.color === "green"
                  ? "#00FF88"
                  : "#FFB800";

              return (
                <div
                  key={feature.title}
                  className="glassmorphic rounded-2xl p-6 hover-lift"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{
                      background: bgColor,
                      border: `2px solid ${borderColor}`,
                    }}
                  >
                    <IconComponent
                      className="w-6 h-6"
                      style={{ color: iconColor }}
                    />
                  </div>
                  <h3 className="font-bold mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-400">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8">
            <aside className="lg:col-span-3">
              <div className="sticky top-24 glassmorphic rounded-2xl p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  Documentation
                </h3>
                <nav className="space-y-2">
                  {docsNavItems.map(({ id, label, icon: IconComponent }) => {
                    const isActive = normalizedActiveSection === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => scrollToSection(id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                          isActive
                            ? "bg-cyan-500/10 border border-cyan-500/30 text-cyan-400"
                            : "hover:bg-gray-800/50 text-gray-400 hover:text-white"
                        }`}
                      >
                        <IconComponent className="w-4 h-4" />
                        <span className="text-sm font-medium">{label}</span>
                      </button>
                    );
                  })}
                </nav>

                <div className="mt-8 pt-8 border-t border-gray-800">
                  <h4 className="text-sm font-bold mb-4 text-gray-400">
                    Developer Resources
                  </h4>
                  <div className="space-y-2">
                    {[
                      { name: "Python SDK", href: "#", icon: "🐍" },
                      { name: "JavaScript SDK", href: "#", icon: "📦" },
                      { name: "Postman Collection", href: "#", icon: "📬" },
                      {
                        name: "API Playground",
                        href: "/dashboard/api-playground",
                        icon: "⚡",
                      },
                      { name: "Changelog", href: "#", icon: "📝" },
                      { name: "API Status", href: "#", icon: "📊" },
                      { name: "GitHub Examples", href: "#", icon: "💻" },
                    ].map((resource) => (
                      <a
                        key={resource.name}
                        href={resource.href}
                        className="block text-sm text-gray-400 hover:text-cyan-400 transition-colors flex items-center gap-2"
                      >
                        <span>{resource.icon}</span>
                        <span>{resource.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            <main className="lg:col-span-9">
              <div className="space-y-24">
                <section id="docs-overview" className="space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold mb-4">API Overview</h2>
                    <p className="text-gray-400 text-lg">
                      The Crypsey API provides programmatic access to Canada's
                      most comprehensive mortgage dataset. Built for developers
                      who need fast, reliable, and well-documented APIs. Whether
                      you're building analytics dashboards, risk models, or
                      fintech applications, our API gives you the data you need
                      with the developer experience you deserve.
                    </p>
                  </div>

                  <div className="glassmorphic rounded-2xl p-8">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Info className="w-5 h-5 text-cyan-400" />
                      Base URL
                    </h3>
                    <div className="code-block">
                      <pre className="text-cyan-400">
                        https://api.mortgagechain.ca/v1
                      </pre>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="glassmorphic rounded-2xl p-6">
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        Developer Features
                      </h3>
                      <ul className="space-y-3 text-sm text-gray-400">
                        {[
                          "RESTful API with intuitive endpoints",
                          "Comprehensive code examples in multiple languages",
                          "Interactive API playground for testing",
                          "SDK libraries for Python and JavaScript",
                          "Postman collection for quick integration",
                          "GraphQL endpoint for flexible queries",
                          "Webhook support for real-time updates",
                          "Detailed error messages and status codes",
                        ].map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <ArrowRight className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="glassmorphic rounded-2xl p-6">
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-purple-400" />
                        Security & Compliance
                      </h3>
                      <ul className="space-y-3 text-sm text-gray-400">
                        {[
                          "PIPEDA compliant data handling",
                          "All data anonymized at source",
                          "TLS 1.3 encryption in transit",
                          "SOC 2 Type II certified",
                        ].map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <ArrowRight className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </section>

                <section id="docs-quickstart" className="space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold mb-4">
                      Quick Start Guide
                    </h2>
                    <p className="text-gray-400 text-lg">
                      Get up and running with the Crypsey API in minutes.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {quickStartSteps.map((step, index) => {
                      const IconComponent = step.icon;
                      const background =
                        step.color === "cyan"
                          ? "rgba(0, 212, 255, 0.1)"
                          : step.color === "purple"
                          ? "rgba(139, 92, 246, 0.1)"
                          : "rgba(0, 255, 136, 0.1)";
                      const border =
                        step.color === "cyan"
                          ? "rgba(0, 212, 255, 0.3)"
                          : step.color === "purple"
                          ? "rgba(139, 92, 246, 0.3)"
                          : "rgba(0, 255, 136, 0.3)";
                      const iconColor =
                        step.color === "cyan"
                          ? "#00D4FF"
                          : step.color === "purple"
                          ? "#8B5CF6"
                          : "#00FF88";

                      return (
                        <div
                          key={step.title}
                          className="glassmorphic rounded-2xl p-8 hover-lift"
                        >
                          <div className="flex items-start gap-6">
                            <div
                              className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                              style={{
                                background,
                                border: `2px solid ${border}`,
                              }}
                            >
                              <IconComponent
                                className="w-8 h-8"
                                style={{ color: iconColor }}
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-sm font-bold text-gray-500">
                                  STEP {index + 1}
                                </span>
                                <h3 className="text-xl font-bold">
                                  {step.title}
                                </h3>
                              </div>
                              <p className="text-gray-400">
                                {step.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="glassmorphic rounded-2xl p-8">
                    <h3 className="text-xl font-bold mb-6">
                      Your First Request
                    </h3>
                    <div className="flex gap-2 mb-4 overflow-x-auto">
                      {(
                        [
                          "curl",
                          "javascript",
                          "python",
                          "ruby",
                        ] as CodeLanguage[]
                      ).map((lang) => (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => setActiveLang(lang)}
                          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                            activeLang === lang
                              ? "tab-active"
                              : "bg-gray-800/50 text-gray-400 hover:bg-gray-800"
                          }`}
                        >
                          {lang.toUpperCase()}
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <div className="code-block">
                        <pre>
                          <code>{codeExamples[activeLang]}</code>
                        </pre>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopyToClipboard(
                            codeExamples[activeLang],
                            "quickstart"
                          )
                        }
                        className="absolute top-4 right-4 p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        {copiedCode === "quickstart" ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </section>

                <section id="docs-authentication" className="space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold mb-4">Authentication</h2>
                    <p className="text-gray-400 text-lg">
                      All API requests require authentication using API keys.
                      Keep your keys secure and never share them publicly.
                    </p>
                  </div>

                  <div className="glassmorphic rounded-2xl p-8">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Key className="w-5 h-5 text-cyan-400" />
                      API Key Authentication
                    </h3>
                    <p className="text-gray-400 mb-6">
                      Include your API key in the Authorization header of every
                      request:
                    </p>
                    <div className="code-block mb-6">
                      <pre>
                        <code className="text-cyan-400">
                          Authorization: Bearer YOUR_API_KEY
                        </code>
                      </pre>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-200">
                        <strong className="block mb-1">Important:</strong>
                        Never expose your API key in client-side code or public
                        repositories. All requests should be made from your
                        server.
                      </div>
                    </div>
                  </div>

                  <div className="glassmorphic rounded-2xl p-8">
                    <h3 className="text-xl font-bold mb-6">
                      Getting Your API Key
                    </h3>
                    <ol className="space-y-4 text-gray-400">
                      {[
                        "Sign up for a Crypsey account",
                        "Navigate to the API Keys section in your dashboard",
                        'Click "Generate New Key" and give it a descriptive name',
                        "Copy and securely store your key (you won't be able to see it again)",
                      ].map((item, index) => (
                        <li key={item} className="flex gap-3">
                          <span className="font-bold text-cyan-400">
                            {index + 1}.
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </section>

                <section id="docs-endpoints" className="space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold mb-4">API Endpoints</h2>
                    <p className="text-gray-400 text-lg">
                      Complete reference of all available endpoints with
                      detailed examples. Each endpoint includes request
                      parameters, response schemas, and code samples in multiple
                      languages. Use the interactive API playground to test
                      endpoints before integrating.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {categories.map((category) => {
                      const categoryKey = category.toLowerCase();
                      const list = endpointsByCategory[category];
                      if (list.length === 0) return null;

                      return (
                        <div
                          key={category}
                          className="glassmorphic rounded-2xl overflow-hidden"
                        >
                          <button
                            type="button"
                            onClick={() => toggleSection(categoryKey)}
                            className="w-full p-6 flex items-center justify-between hover:bg-gray-800/30 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <h3 className="text-xl font-bold">{category}</h3>
                              <span className="px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-xs font-bold text-cyan-400">
                                {list.length} endpoints
                              </span>
                            </div>
                            {expandedSections[categoryKey] ? (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            )}
                          </button>

                          {expandedSections[categoryKey] && (
                            <div className="border-t border-gray-800">
                              {list.map((endpoint) => (
                                <div
                                  key={endpoint.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() =>
                                    setSelectedEndpoint(endpoint.id)
                                  }
                                  onKeyDown={(event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === " "
                                    ) {
                                      setSelectedEndpoint(endpoint.id);
                                    }
                                  }}
                                  className="p-6 border-b border-gray-800 last:border-b-0 hover:bg-gray-800/30 transition-colors cursor-pointer"
                                >
                                  <div className="flex items-start gap-4 mb-3">
                                    <span
                                      className={`method-badge method-${endpoint.method.toLowerCase()}`}
                                    >
                                      {endpoint.method}
                                    </span>
                                    <div className="flex-1">
                                      <h4 className="font-bold mb-1">
                                        {endpoint.title}
                                      </h4>
                                      <code className="text-sm text-cyan-400 font-mono">
                                        {endpoint.path}
                                      </code>
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-400 ml-16">
                                    {endpoint.description}
                                  </p>

                                  {selectedEndpoint === endpoint.id && (
                                    <div className="mt-6 ml-16 space-y-6">
                                      {/* Request Example */}
                                      <div>
                                        <h5 className="font-bold mb-3 text-sm flex items-center gap-2">
                                          <Terminal className="w-4 h-4 text-cyan-400" />
                                          Request Example
                                        </h5>
                                        <div className="flex gap-2 mb-4 overflow-x-auto">
                                          {(
                                            [
                                              "curl",
                                              "javascript",
                                              "python",
                                              "ruby",
                                            ] as CodeLanguage[]
                                          ).map((lang) => (
                                            <button
                                              key={lang}
                                              type="button"
                                              onClick={() =>
                                                setActiveLang(lang)
                                              }
                                              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                                activeLang === lang
                                                  ? "tab-active"
                                                  : "bg-gray-800/50 text-gray-400 hover:bg-gray-800"
                                              }`}
                                            >
                                              {lang.toUpperCase()}
                                            </button>
                                          ))}
                                        </div>
                                        <div className="relative">
                                          <div className="code-block">
                                            <pre>
                                              <code>
                                                {getCodeExample(
                                                  endpoint,
                                                  activeLang
                                                )}
                                              </code>
                                            </pre>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              handleCopyToClipboard(
                                                getCodeExample(
                                                  endpoint,
                                                  activeLang
                                                ),
                                                `${endpoint.id}-request`
                                              );
                                            }}
                                            className="absolute top-4 right-4 p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                                          >
                                            {copiedCode ===
                                            `${endpoint.id}-request` ? (
                                              <Check className="w-4 h-4 text-green-400" />
                                            ) : (
                                              <Copy className="w-4 h-4 text-gray-400" />
                                            )}
                                          </button>
                                        </div>
                                      </div>

                                      {endpoint.parameters &&
                                        endpoint.parameters.length > 0 && (
                                          <div>
                                            <h5 className="font-bold mb-3 text-sm">
                                              Parameters
                                            </h5>
                                            <div className="space-y-3">
                                              {endpoint.parameters.map(
                                                (param) => (
                                                  <div
                                                    key={param.name}
                                                    className="bg-[#0A0E27]/70 rounded-lg p-4"
                                                  >
                                                    <div className="flex items-center gap-3 mb-2">
                                                      <code className="text-cyan-400 font-mono text-sm">
                                                        {param.name}
                                                      </code>
                                                      <span className="px-2 py-0.5 bg-purple-500/10 border border-purple-500/30 rounded text-xs text-purple-400">
                                                        {param.type}
                                                      </span>
                                                      {param.required && (
                                                        <span className="px-2 py-0.5 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                                                          required
                                                        </span>
                                                      )}
                                                      {param.default && (
                                                        <span className="text-xs text-gray-500">
                                                          default:{" "}
                                                          {param.default}
                                                        </span>
                                                      )}
                                                    </div>
                                                    <p className="text-sm text-gray-400">
                                                      {param.description}
                                                    </p>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          </div>
                                        )}

                                      <div>
                                        <div className="flex items-center justify-between mb-3">
                                          <h5 className="font-bold text-sm">
                                            Response Example
                                          </h5>
                                          <button
                                            type="button"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              handleCopyToClipboard(
                                                JSON.stringify(
                                                  endpoint.response,
                                                  null,
                                                  2
                                                ),
                                                `${endpoint.id}-response`
                                              );
                                            }}
                                            className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                                          >
                                            {copiedCode ===
                                            `${endpoint.id}-response` ? (
                                              <Check className="w-4 h-4 text-green-400" />
                                            ) : (
                                              <Copy className="w-4 h-4 text-gray-400" />
                                            )}
                                          </button>
                                        </div>
                                        <div className="code-block">
                                          <pre>
                                            <code className="text-sm">
                                              {JSON.stringify(
                                                endpoint.response,
                                                null,
                                                2
                                              )}
                                            </code>
                                          </pre>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section id="docs-errors" className="space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold mb-4">Error Handling</h2>
                    <p className="text-gray-400 text-lg">
                      Understanding API error responses and how to handle them.
                    </p>
                  </div>

                  <div className="glassmorphic rounded-2xl p-8">
                    <h3 className="text-xl font-bold mb-6">
                      Error Response Format
                    </h3>
                    <div className="code-block">
                      <pre>
                        <code>{`{
  "error": {
    "code": "invalid_request",
    "message": "Missing required parameter: loan_id",
    "details": {
      "param": "loan_id",
      "location": "path"
    }
  }
}`}</code>
                      </pre>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {[
                      {
                        code: "200",
                        title: "Success",
                        description: "Request completed successfully",
                        color: "green",
                      },
                      {
                        code: "400",
                        title: "Bad Request",
                        description: "Invalid request parameters",
                        color: "amber",
                      },
                      {
                        code: "401",
                        title: "Unauthorized",
                        description: "Missing or invalid API key",
                        color: "red",
                      },
                      {
                        code: "403",
                        title: "Forbidden",
                        description: "Insufficient permissions",
                        color: "red",
                      },
                      {
                        code: "404",
                        title: "Not Found",
                        description: "Resource not found",
                        color: "amber",
                      },
                      {
                        code: "429",
                        title: "Rate Limited",
                        description: "Too many requests",
                        color: "amber",
                      },
                      {
                        code: "500",
                        title: "Server Error",
                        description: "Internal server error",
                        color: "red",
                      },
                      {
                        code: "503",
                        title: "Unavailable",
                        description: "Service temporarily unavailable",
                        color: "red",
                      },
                    ].map((status) => (
                      <div
                        key={status.code}
                        className="glassmorphic rounded-xl p-6"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span
                            className={`px-3 py-1 rounded-lg font-mono text-sm font-bold ${
                              status.color === "green"
                                ? "bg-green-500/10 text-green-400 border border-green-500/30"
                                : status.color === "amber"
                                ? "bg-amber-500/10 text-amber-400 border border-amber-500/30"
                                : "bg-red-500/10 text-red-400 border border-red-500/30"
                            }`}
                          >
                            {status.code}
                          </span>
                          <h4 className="font-bold">{status.title}</h4>
                        </div>
                        <p className="text-sm text-gray-400">
                          {status.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                <section id="docs-rate-limits" className="space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold mb-4">Rate Limits</h2>
                    <p className="text-gray-400 text-lg">
                      API usage limits based on your subscription tier.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    {[
                      {
                        tier: "Professional",
                        requests: "1,000",
                        period: "per month",
                      },
                      { tier: "Enterprise", requests: "Unlimited", period: "" },
                      { tier: "Institutional", requests: "Custom", period: "" },
                    ].map((plan) => (
                      <div
                        key={plan.tier}
                        className="glassmorphic rounded-2xl p-6"
                      >
                        <h3 className="font-bold mb-4">{plan.tier}</h3>
                        <div className="text-3xl font-bold gradient-text mb-2 font-mono">
                          {plan.requests}
                        </div>
                        {plan.period && (
                          <p className="text-sm text-gray-400">{plan.period}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="glassmorphic rounded-2xl p-8">
                    <h3 className="text-xl font-bold mb-4">
                      Rate Limit Headers
                    </h3>
                    <p className="text-gray-400 mb-6">
                      Every API response includes headers showing your current
                      usage:
                    </p>
                    <div className="code-block">
                      <pre>
                        <code>{`X-RateLimit-Limit: 1000
X-RateLimit-Remaining: 847
X-RateLimit-Reset: 1640995200`}</code>
                      </pre>
                    </div>
                  </div>
                </section>

                <section id="docs-webhooks" className="space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold mb-4">Webhooks</h2>
                    <p className="text-gray-400 text-lg">
                      Receive real-time notifications about data updates and
                      events.
                    </p>
                  </div>

                  <div className="glassmorphic rounded-2xl p-8">
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 flex items-start gap-3">
                      <Info className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-cyan-200">
                        <strong className="block mb-1">Coming Soon</strong>
                        Webhook support is currently in development. Subscribe
                        to our changelog for updates.
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </main>
          </div>
        </div>
      </section>

      <MortgageChainFooter
        onScheduleCall={() => emitFormEvent("call")}
        onViewDeck={() => emitFormEvent("deck")}
      />
    </div>
  );
}
